// Admin chat (support): a user's direct line to an administrator -- a concern,
// a bug report, anything. A text thread watched on the page's socket (the
// channel 'support:<requestId>', pushed the moment a reply lands; it used to
// be asked for every second), plus an admin-initiated live voice/video
// session the user must explicitly accept. The requestId persists locally so
// a closed tab or locked phone resumes the same conversation. ASCII only.

import { t } from '../i18n.js';
import { el, ORB_SVG } from '../dom.js';
import { SERVER_HTTP } from '../config.js';
import { getFingerprint } from '../device-signal.js';
import { channels } from '../socket-channels.js';

const SUPPORT_KEY = 'lumi.supportId';

function getSavedId() {
  try {
    return localStorage.getItem(SUPPORT_KEY) || '';
  } catch {
    return '';
  }
}
function saveId(id) {
  try {
    if (id) localStorage.setItem(SUPPORT_KEY, id);
    else localStorage.removeItem(SUPPORT_KEY);
  } catch {
    /* fine */
  }
}

async function apiStart(text, handle) {
  // The server limits starting a thread per device, so name the device, as
  // account recovery does. One that cannot be read is sent empty.
  const fingerprint = await getFingerprint().catch(() => '');
  const res = await fetch(`${SERVER_HTTP}/api/support/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, handle, fingerprint }),
  });
  if (!res.ok) throw new Error(res.status === 403 ? 'disabled' : 'failed');
  return res.json();
}
/**
 * The thread as the route says it, for the moments no socket carries the
 * channel. A 404 is the thread being gone, which the channel says as
 * { error: 'not_found' }; anything else that is not an answer is null, and
 * the asker backs off.
 */
async function apiStatus(requestId) {
  const res = await fetch(`${SERVER_HTTP}/api/support/status?requestId=${encodeURIComponent(requestId)}`);
  if (res.status === 404) return { error: 'not_found' };
  if (!res.ok) return null;
  return res.json();
}
/**
 * Send one reply. Returns { ok: true, data } or { ok: false, error }.
 *
 * It used to return null for every failure, and the caller did nothing at all
 * with a null -- so a message sent to a conversation the operator had just
 * closed vanished with no error, no line in the thread, and no explanation.
 * The reason has to come back for the caller to be able to say anything useful.
 */
async function apiReply(requestId, text) {
  const res = await fetch(`${SERVER_HTTP}/api/support/reply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ requestId, text }),
  });
  if (res.ok) return { ok: true, data: await res.json().catch(() => ({})) };
  let error = 'failed';
  try {
    error = (await res.json())?.error || 'failed';
  } catch {
    /* a body that is not JSON tells us nothing more than the status did */
  }
  return { ok: false, error };
}
async function apiLiveAnswer(requestId, answer) {
  const res = await fetch(`${SERVER_HTTP}/api/support/live-answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ requestId, answer }),
  });
  return res.ok;
}

export function renderSupport({ onBack, handle }) {
  let requestId = getSavedId();
  /** The leaving of the channel and of the asking, while a thread is watched. */
  let unwatch = null;
  let stopped = false;
  let renderedSig = '';
  let liveHandledOffer = '';
  let livePc = null;
  let liveMedia = null;

  const title = el('h2', { text: t('Chat with an Admin') });
  const sub = el('p', {
    class: 'verify-sub',
    text: t('A concern, a bug or a question. It goes straight to the Lumi team. Replies appear here; keep the page open.'),
  });

  // ---- Start form (hidden once a thread exists) ----
  const startText = el('textarea', {
    class: 'support-start-text',
    maxlength: '1000',
    rows: '3',
    placeholder: t('What would you like to tell us?'),
    aria: { label: 'What would you like to tell us?' },
  });
  const startBtn = el('button', { class: 'btn btn-primary', type: 'button', text: t('Send to an admin') });
  const startForm = el('div', { class: 'support-start' }, [startText, startBtn]);

  // ---- Thread ----
  const msgsBox = el('div', { class: 'verify-chat-msgs support-msgs' });
  const replyInput = el('input', { type: 'text', maxlength: '1000', placeholder: t('Type a message...'), aria: { label: 'Your reply' } });
  const replySend = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: t('Send') });
  const thread = el('div', { class: 'verify-chat support-thread hidden' }, [
    msgsBox,
    el('div', { class: 'verify-chat-row' }, [replyInput, replySend]),
  ]);

  // ---- Live session (admin-initiated; user must accept) ----
  const liveSelf = el('video', { autoplay: '', playsinline: '', muted: '', class: 'verify-live-self hidden' });
  const remoteMedia = el('video', { autoplay: '', playsinline: '', class: 'support-remote hidden' });
  const liveStatus = el('div', { class: 'verify-sub', text: '' });
  const liveStartBtn = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: t('Start live chat') });
  const liveNoSoundBtn = el('button', { class: 'btn btn-ghost btn-sm hidden', type: 'button', text: t('Continue without sound') });
  const liveBox = el('div', { class: 'verify-live hidden' }, [
    el('div', { class: 'verify-chat-title', text: t('The admin wants to talk live') }),
    el('p', { class: 'verify-sub', text: t('Voice and video, so you can talk directly. Nothing starts until you tap.') }),
    remoteMedia,
    liveSelf,
    liveStatus,
    liveStartBtn,
    liveNoSoundBtn,
  ]);
  function stopLive() {
    try { livePc?.close(); } catch { /* closed */ }
    livePc = null;
    liveMedia?.getTracks().forEach((track) => track.stop());
    liveMedia = null;
  }
  const waitIce = (pc, ms) =>
    new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const iceTimer = setTimeout(resolve, ms);
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') { clearTimeout(iceTimer); resolve(); }
      });
    });
  // Front camera as a hard requirement (a soft preference can lose to other
  // constraints and open the REAR camera); fall back for devices without one.
  const getFrontMedia = async (withAudio) => {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: 'user' } },
        audio: withAudio,
      });
    } catch (err) {
      if (err && (err.name === 'OverconstrainedError' || err.name === 'NotFoundError')) {
        return navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: withAudio });
      }
      throw err;
    }
  };
  let pendingLive = null;
  async function startLive(req, skipMic = false) {
    liveStartBtn.disabled = true;
    liveNoSoundBtn.classList.add('hidden');
    liveStatus.textContent = 'Starting your camera...';
    // A blocked mic pauses for a decision (the offer is not answered yet, so
    // fixing the permission and trying again works cleanly). "Continue without
    // sound" still TRIES the mic -- the browser may prompt right then and the
    // user may allow it, in which case the sound must actually be used.
    if (!skipMic) {
      try {
        liveMedia = await getFrontMedia(true);
      } catch {
        stopLive();
        liveStartBtn.disabled = false;
        liveStartBtn.textContent = 'Try again with sound';
        liveNoSoundBtn.classList.remove('hidden');
        liveStatus.textContent =
          'Your microphone is blocked, so the admin would not hear you. Allow the microphone for this site (lock icon by the address bar), then tap Try again with sound.';
        return;
      }
    } else {
      try {
        liveMedia = await getFrontMedia(true);
      } catch {
        try {
          liveMedia = await getFrontMedia(false);
        } catch (err) {
          stopLive();
          liveStartBtn.disabled = false;
          liveStatus.textContent =
            err && err.name === 'NotAllowedError'
              ? 'Camera access is blocked for this site. Allow it in your browser settings and try again.'
              : `Could not start your camera (${(err && err.name) || 'unknown error'}).`;
          return;
        }
      }
    }
    // Trust the stream, not the button: sound is on iff an audio track exists.
    const hasMic = (liveMedia.getAudioTracks?.() || []).length > 0;
    try {
      const cfg = req.ice ? { iceServers: req.ice.iceServers, iceTransportPolicy: req.ice.iceTransportPolicy } : {};
      livePc = new RTCPeerConnection(cfg);
      for (const track of liveMedia.getTracks()) livePc.addTrack(track, liveMedia);
      livePc.ontrack = (e) => {
        if (e.streams[0]) {
          remoteMedia.srcObject = e.streams[0];
          remoteMedia.classList.remove('hidden');
        }
      };
      livePc.onconnectionstatechange = () => {
        if (['failed', 'disconnected', 'closed'].includes(livePc?.connectionState)) {
          liveStatus.textContent = 'The live chat ended.';
          liveSelf.classList.add('hidden');
          remoteMedia.classList.add('hidden');
          stopLive();
        }
      };
      await livePc.setRemoteDescription(JSON.parse(req.offer));
      const answer = await livePc.createAnswer();
      await livePc.setLocalDescription(answer);
      await waitIce(livePc, 4000);
      const ok = await apiLiveAnswer(requestId, JSON.stringify(livePc.localDescription));
      if (!ok) throw new Error('send failed');
      liveSelf.srcObject = liveMedia;
      liveSelf.classList.remove('hidden');
      liveStartBtn.classList.add('hidden');
      liveNoSoundBtn.classList.add('hidden');
      liveStatus.textContent = hasMic
        ? 'Live: the admin can see and hear you.'
        : 'Live: the admin can see you (your mic is blocked; you can still type).';
    } catch (err) {
       
      console.warn('support live failed', err);
      stopLive();
      liveStartBtn.disabled = false;
      liveStatus.textContent = `Could not start the live chat (${(err && err.name) || 'error'}). Try again.`;
    }
  }
  liveStartBtn.addEventListener('click', () => {
    if (pendingLive) startLive(pendingLive);
  });
  liveNoSoundBtn.addEventListener('click', () => {
    if (pendingLive) startLive(pendingLive, true);
  });

  function renderMsgs(list) {
    const sig = JSON.stringify(list);
    if (sig === renderedSig) return;
    renderedSig = sig;
    msgsBox.replaceChildren(
      ...list.map((m) =>
        el('div', { class: `verify-chat-msg ${m.from === 'admin' ? 'from-admin' : 'from-you'}` }, [
          el('strong', { text: m.from === 'admin' ? 'Admin: ' : 'You: ' }),
          el('span', { text: m.text }),
        ]),
      ),
    );
    thread.classList.remove('hidden');
    startForm.classList.add('hidden');
    msgsBox.scrollTop = msgsBox.scrollHeight;
  }

  /** Stop watching the thread, if one is watched. */
  function stopWatching() {
    unwatch?.();
    unwatch = null;
  }

  /** What the thread says now: the same frame from the socket or the route. */
  function applyThread(data) {
    if (stopped || !requestId || !data) return;
    if (data.error === 'not_found') {
      // The request is gone (e.g. wiped store): start fresh.
      stopWatching();
      saveId('');
      requestId = '';
      startForm.classList.remove('hidden');
      return;
    }
    if (data.status === 'closed') {
      stopWatching();
      saveId('');
      requestId = '';
      // Whatever they were part-way through typing moves with them. The
      // close and the send race each other when a thread is closed mid
      // sentence; whichever gets there first, the words survive, or the
      // reply box is simply hidden with their message still in it.
      const pending = replyInput.value.trim();
      if (pending && !startText.value.trim()) startText.value = pending;
      replyInput.value = '';
      sub.textContent = pending
        ? 'That conversation was closed by the team, so this did not send. Your message is below. Send it to start a new one.'
        : 'This conversation was closed by the team. Start a new one anytime.';
      liveBox.classList.add('hidden');
      stopLive();
      thread.classList.add('hidden');
      startForm.classList.remove('hidden');
      return;
    }
    if (Array.isArray(data.messages) && data.messages.length) renderMsgs(data.messages);
    if (data.liveOffer && data.liveOffer !== liveHandledOffer) {
      liveHandledOffer = data.liveOffer;
      stopLive();
      pendingLive = { offer: data.liveOffer, ice: data.ice || null };
      liveStartBtn.disabled = false;
      liveStartBtn.textContent = 'Start live chat';
      liveStartBtn.classList.remove('hidden');
      liveNoSoundBtn.classList.add('hidden');
      liveBox.classList.remove('hidden');
    }
  }

  /**
   * Watch the thread on the page's socket: answered at once, and again on
   * every change. While no socket carries it, the route is asked instead.
   */
  function watchThread() {
    stopWatching();
    if (stopped || !requestId) return;
    const id = requestId;
    const offChannel = channels.subscribe(`support:${id}`, applyThread);
    const offAsking = channels.askWhileUnbound(() => apiStatus(id), applyThread);
    unwatch = () => {
      offChannel();
      offAsking();
    };
  }

  startBtn.addEventListener('click', async () => {
    const text = startText.value.trim();
    if (!text) return;
    startBtn.disabled = true;
    try {
      const data = await apiStart(text, handle || '');
      requestId = data.requestId;
      saveId(requestId);
      startText.value = '';
      renderMsgs(data.messages || []);
      watchThread();
    } catch (err) {
      sub.textContent =
        String(err?.message) === 'disabled'
          ? 'Admin chat is not available right now. Please try again later.'
          : 'Could not send that. Please check your connection and try again.';
    } finally {
      startBtn.disabled = false;
    }
  });

  async function submitReply() {
    const text = replyInput.value.trim();
    if (!text || !requestId || replySend.disabled) return;
    replySend.disabled = true;
    const res = await apiReply(requestId, text);
    replySend.disabled = false;

    if (res.ok) {
      replyInput.value = '';
      renderMsgs(res.data.messages || []);
      return;
    }

    // The conversation was closed while they were typing. Nothing they did is
    // wrong and nothing they wrote should be lost: the text moves into the
    // start box, so pressing send again opens a fresh conversation with it
    // rather than making them type it a second time.
    if (res.error === 'closed' || res.error === 'not_found') {
      stopWatching();
      saveId('');
      requestId = '';
      stopLive();
      liveBox.classList.add('hidden');
      thread.classList.add('hidden');
      startForm.classList.remove('hidden');
      startText.value = text;
      replyInput.value = '';
      sub.textContent =
        res.error === 'closed'
          ? 'That conversation was closed by the team, so this did not send. Your message is below. Send it to start a new one.'
          : 'That conversation is no longer available, so this did not send. Your message is below. Send it to start a new one.';
      startText.focus();
      return;
    }

    // Anything else is a connection problem. Say so, and leave what they typed
    // exactly where it is so it can be sent again.
    sub.textContent = 'Could not send that. Please check your connection and try again.';
  }
  replySend.addEventListener('click', submitReply);
  replyInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submitReply();
  });

  const root = el('div', { class: 'verify support-screen' }, [
    el('div', { class: 'verify-inner' }, [
      el('div', { class: 'topbar' }, [el('span', { html: ORB_SVG }), 'Lumi']),
      el('div', { class: 'verify-card' }, [
        title,
        sub,
        startForm,
        thread,
        liveBox,
        el('button', {
          class: 'btn btn-ghost',
          type: 'button',
          text: t('Back'),
          onClick: () => {
            stopped = true;
            stopWatching();
            stopLive();
            onBack();
          },
        }),
      ]),
      el('p', {
        class: 'legal',
        text: t('Messages here go only to Lumi administrators. A live voice/video chat starts only if you tap Start.'),
      }),
    ]),
  ]);

  // Resume an existing conversation if one is open.
  if (requestId) watchThread();

  return { root };
}
